import type { RefObject } from "react";
import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { combineValidators, maxCharactersValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import type { CustomColumnField, ReportField } from "../../../../../../../../../../api/types/svTypes";
import { DimensionFieldType } from "../../../../../../../../../../api/types/svTypes";
import { selectFieldsForCalculation } from "../../../../../../store/selectors/fieldsStateSelectors";
import { getFieldCaption } from "../../../../../../utilities/Utilities";
import type { CustomColumnData, CustomFormulaReferenceOption } from "../customColumn.types";
import { insertFieldIntoFormula, insertFunctionIntoFormula } from "../utilities/customFormulaFieldInsert";
import type { FormulaFunctionDefinition } from "../utilities/customFormulaFunctions";
import { getFormulaFunctionDefinitions } from "../utilities/customFormulaFunctions";
import { getCustomFormulaDialogInitialValues } from "../utilities/customFormulaInitialValues";
import type { FormulaReferenceField } from "../utilities/customFormulaParser";
import { parseCustomFormula } from "../utilities/customFormulaParser";
import { buildCustomFormulaApplyPayload } from "../utilities/customFormulaPayload";

interface Params {
  editedField?: CustomColumnField;
  sourceFieldGuid?: string;
  referableFields?: FormulaReferenceField[];
  allowGrandTotal?: boolean;
  getReferenceError?: (formulaParameters: string[]) => string | undefined;
  onApply: (data: CustomColumnData) => void;
}

interface Result {
  name: string;
  nameError?: string;
  formulaInput: string;
  formulaError?: string;
  format: CustomColumnData["format"];
  blankIfMissingValue: boolean;
  filteredFieldsForCalculation: CustomFormulaReferenceOption[];
  formulaReferenceFields: FormulaReferenceField[];
  formulaFunctions: FormulaFunctionDefinition[];
  formulaFunctionNames: string[];
  formulaInputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  isApplyDisabled: boolean;
  handleNameChange: (value: string) => void;
  handleNameBlur: () => void;
  handleSearchChange: (value: string) => void;
  handleFormatChange: (value: CustomColumnData["format"]) => void;
  handleFormulaChange: (value: string) => void;
  handleFormulaBlur: () => void;
  handleFieldSelect: (fieldCaption: string) => void;
  handleFunctionSelect: (fn: FormulaFunctionDefinition) => void;
  handleBlankIfMissingValueChange: (value: boolean) => void;
  handleApply: () => void;
}

const validateName = combineValidators(requiredValidator, maxCharactersValidator(255));

const focusInputAt = (ref: RefObject<HTMLInputElement | HTMLTextAreaElement | null>, index: number): void => {
  requestAnimationFrame(() => {
    if (!ref.current) {
      return;
    }
    ref.current.focus();
    ref.current.setSelectionRange(index, index);
  });
};

const toReferenceOption = (field: ReportField): CustomFormulaReferenceOption => ({
  guid: field.config.guid,
  caption: getFieldCaption(field),
  icon: field.meta.icon,
  isCustomMeasure: field.type === DimensionFieldType.MEASURE && "custom" in field.meta && field.meta.custom === true,
});

const useCustomFormulaDialogForm = ({
  editedField,
  sourceFieldGuid,
  referableFields,
  allowGrandTotal = true,
  getReferenceError,
  onApply,
}: Params): Result => {
  const formulaInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const fieldsForCalculation = useSelector(selectFieldsForCalculation);
  const formulaReferenceFields = useMemo<CustomFormulaReferenceOption[]>(
    () => referableFields ?? fieldsForCalculation.map(toReferenceOption),
    [fieldsForCalculation, referableFields]
  );
  const formulaFunctionNames = useMemo(
    () => getFormulaFunctionDefinitions(allowGrandTotal).map((fn) => fn.name),
    [allowGrandTotal]
  );
  const initialValues = getCustomFormulaDialogInitialValues(editedField, formulaReferenceFields, sourceFieldGuid);

  const [name, setName] = useState(initialValues.name);
  const [nameTouched, setNameTouched] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [formulaInput, setFormulaInput] = useState(initialValues.formulaInput);
  const [formulaTouched, setFormulaTouched] = useState(false);
  const [format, setFormat] = useState<CustomColumnData["format"]>(initialValues.format);
  const [blankIfMissingValue, setBlankIfMissingValue] = useState(initialValues.blankIfMissingValue);

  const nameValidationResult = validateName(name);
  const parsedFormulaResult = useMemo(
    () => parseCustomFormula(formulaInput, formulaReferenceFields, formulaFunctionNames),
    [formulaInput, formulaReferenceFields, formulaFunctionNames]
  );
  const referenceError =
    getReferenceError && parsedFormulaResult.parsed
      ? getReferenceError(parsedFormulaResult.parsed.formulaParameters)
      : undefined;
  const formulaValidationError = parsedFormulaResult.isValid ? referenceError : parsedFormulaResult.error;
  const formulaError = formulaTouched ? formulaValidationError : undefined;
  const nameError = nameTouched && !nameValidationResult.isValid ? nameValidationResult.error : undefined;
  const filteredFieldsForCalculation = formulaReferenceFields.filter((field) =>
    field.caption.toLowerCase().includes(searchText.trim().toLowerCase())
  );

  const handleFormulaBlur = () => {
    setFormulaTouched(true);
    if (parsedFormulaResult.isValid && parsedFormulaResult.parsed) {
      setFormulaInput(parsedFormulaResult.parsed.displayFormula);
    }
  };

  const handleFieldSelect = (fieldCaption: string) => {
    setFormulaInput(insertFieldIntoFormula(formulaInput, fieldCaption));
  };

  const handleFunctionSelect = (fn: FormulaFunctionDefinition) => {
    const newFormula = insertFunctionIntoFormula(formulaInput, fn.insertText);
    setFormulaInput(newFormula);
    focusInputAt(formulaInputRef, newFormula.length - 1);
  };

  const handleApply = () => {
    setNameTouched(true);
    setFormulaTouched(true);
    if (
      !nameValidationResult.isValid ||
      !parsedFormulaResult.isValid ||
      !parsedFormulaResult.parsed ||
      referenceError !== undefined
    ) {
      return;
    }

    const data = buildCustomFormulaApplyPayload(name, parsedFormulaResult.parsed, format, blankIfMissingValue);
    onApply(data);
  };

  return {
    name,
    nameError,
    formulaInput,
    formulaError,
    format,
    blankIfMissingValue,
    filteredFieldsForCalculation,
    formulaReferenceFields,
    formulaFunctions: getFormulaFunctionDefinitions(allowGrandTotal),
    formulaFunctionNames,
    formulaInputRef,
    isApplyDisabled: !nameValidationResult.isValid || !parsedFormulaResult.isValid || referenceError !== undefined,
    handleNameChange: setName,
    handleNameBlur: () => setNameTouched(true),
    handleSearchChange: setSearchText,
    handleFormatChange: setFormat,
    handleFormulaChange: setFormulaInput,
    handleFormulaBlur,
    handleFieldSelect,
    handleFunctionSelect,
    handleBlankIfMissingValueChange: setBlankIfMissingValue,
    handleApply,
  };
};

export default useCustomFormulaDialogForm;
